import { parse, TYPE } from "@formatjs/icu-messageformat-parser"
import { type Glossary, termsInValue, approvedRenderings } from "./glossary"

export type Severity = "error" | "warning"

export type IssueCode =
  | "placeholder-mismatch"
  | "tag-mismatch"
  | "whitespace"
  | "length"
  | "glossary"

export interface ValueIssue {
  code: IssueCode
  severity: Severity
  message: string
}

export interface CheckValueInput {
  key: string
  locale: string
  enValue: string
  targetValue: string
  glossary: Glossary
}

/** Length ceiling = factor × source length; tighter for label-sized namespaces. */
const TIGHT_NAMESPACES = new Set(["ui", "nav", "cta"])
function lengthFactor(key: string): number {
  return TIGHT_NAMESPACES.has(key.split(".")[0]) ? 1.4 : 2.0
}

/**
 * Namespaces whose values are whole HTML documents (T&C / privacy / imprint),
 * not flat ICU strings. The ICU parser treats `<tag>` as rich-text syntax and
 * THROWS on real HTML / unbalanced braces, and tag-parity is meaningless across
 * locales for a full document — so the placeholder + tag checks are skipped for
 * these keys entirely. Shared with `scripts/lint-translation-values.ts`.
 */
export const NON_ICU_NAMESPACES = new Set(["legalHtml"])

function namespaceOf(key: string): string {
  return key.split(".")[0]
}

/**
 * Top-level ICU argument names in a message (recurses into plural/select).
 * Element types come from the parser's exported `TYPE` enum — `argument` is a
 * bare `{name}`, `select`/`plural` carry sub-options whose branches may nest
 * further args. A dropped/renamed arg is the load-bearing error check.
 *
 * The parser throws a `SyntaxError` on content that isn't valid ICU (raw HTML,
 * a literal unescaped `{`). This runs in the sign-off / apply REQUEST PATH, where
 * a throw would 500 a reviewer mid-approval — strictly worse than skipping
 * placeholder parity on a string that, by virtue of not parsing, has no
 * interpolated args to compare. So a parse failure yields the empty set. This is
 * deliberate input-tolerance for untrusted message content, not defensive
 * masking of a logic bug (the systematic case — `legalHtml` — is excluded up
 * front in `checkValue`; this only backstops a stray literal-brace string).
 */
function icuArgs(value: string): Set<string> {
  const out = new Set<string>()
  const walk = (els: ReturnType<typeof parse>): void => {
    for (const el of els) {
      if (el.type === TYPE.argument) out.add(el.value)
      if (el.type === TYPE.select || el.type === TYPE.plural) {
        out.add(el.value)
        for (const opt of Object.values(el.options)) walk(opt.value)
      }
    }
  }
  let parsed: ReturnType<typeof parse>
  try {
    parsed = parse(value)
  } catch {
    return out // non-ICU content → no comparable args
  }
  walk(parsed)
  return out
}

function isSubset(a: Set<string>, b: Set<string>): boolean {
  return Array.from(a).every((x) => b.has(x))
}

/** Multiset of tag names (`<x>`, `</x>`, `<x/>`) in a string. */
function tagCounts(value: string): Map<string, number> {
  const out = new Map<string, number>()
  for (const m of value.matchAll(/<\/?([a-zA-Z][\w-]*)\b[^>]*>/g)) {
    const name = m[1].toLowerCase()
    out.set(name, (out.get(name) ?? 0) + 1)
  }
  return out
}

function tagsEqual(a: Map<string, number>, b: Map<string, number>): boolean {
  if (a.size !== b.size) return false
  return Array.from(a).every(([k, v]) => b.get(k) === v)
}

function leadTrail(value: string): string {
  const lead = value.length - value.trimStart().length
  const trail = value.length - value.trimEnd().length
  return `${lead}:${trail}`
}

export function checkValue(input: CheckValueInput): ValueIssue[] {
  const { key, locale, enValue, targetValue, glossary } = input
  const issues: ValueIssue[] = []

  // Whole-HTML-document namespaces (legal T&C/privacy/imprint) aren't flat ICU
  // strings: the parser chokes on their markup and tag-parity is meaningless
  // across locales. Skip the placeholder + tag checks for them (the others below
  // still run — they're harmless on HTML).
  const checkIcuAndTags = !NON_ICU_NAMESPACES.has(namespaceOf(key))

  // placeholder / ICU parity — ERROR when the target references an arg the
  // English source doesn't: call sites pass exactly the EN args (tsc-enforced),
  // so an extra target-side arg is a runtime IntlError in that locale. The
  // reverse — target using FEWER args — is runtime-safe (unused values are
  // ignored) and deliberate in the EUR mirrors, where price-bearing strings
  // drop the {price} clause or are empty entirely (pre-launch markets hide
  // prices; see buildFallback in lib/pricing.ts).
  if (checkIcuAndTags && !isSubset(icuArgs(targetValue), icuArgs(enValue))) {
    issues.push({
      code: "placeholder-mismatch",
      severity: "error",
      message: "Placeholders don't match the English source.",
    })
  }

  // tag parity — ERROR
  if (
    checkIcuAndTags &&
    !tagsEqual(tagCounts(enValue), tagCounts(targetValue))
  ) {
    issues.push({
      code: "tag-mismatch",
      severity: "error",
      message: "HTML/rich-text tags don't match the English source.",
    })
  }

  // whitespace — WARNING
  if (leadTrail(enValue) !== leadTrail(targetValue)) {
    issues.push({
      code: "whitespace",
      severity: "warning",
      message: "Leading/trailing whitespace differs from the source.",
    })
  }

  // length — WARNING
  if (targetValue.length > Math.ceil(enValue.length * lengthFactor(key))) {
    issues.push({
      code: "length",
      severity: "warning",
      message: "Translation is much longer than the source — check it fits.",
    })
  }

  // glossary — WARNING ALWAYS (never blocks, even on legal): German inflection
  // means a whole-word match legitimately misses a correct rendering, so this is
  // a consistency nudge for the reviewer, not a gate.
  for (const term of termsInValue(enValue, glossary)) {
    const approved = approvedRenderings(term, locale)
    if (approved.length === 0) continue // no approved rendering for this locale → nothing to assert
    const present = approved.some((r) =>
      new RegExp(`\\b${r.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i").test(
        targetValue
      )
    )
    if (!present) {
      issues.push({
        code: "glossary",
        severity: "warning",
        message: `Term "${term.term}" should use the approved translation.`,
      })
    }
  }

  return issues
}

export function errorsOf(issues: ValueIssue[]): ValueIssue[] {
  return issues.filter((i) => i.severity === "error")
}

export function hasBlockingErrors(issues: ValueIssue[]): boolean {
  return issues.some((i) => i.severity === "error")
}
